import React, { useState, useEffect, useMemo } from "react";
import { getAllSeries, searchSeries } from "../services/seriesService";
import SeriesCard from "./SeriesCard";
import Loader from "./Loader/Loader";
import Pagination from "./Pagination";

const SeriesList = ({
  searchTerm = "",
  genreFilter = "",
  series: propSeries = null,
}) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 15; // 👈 constante

  // Reset de página cuando cambian las entradas de filtrado o el source
  useEffect(() => {
    setCurrentPage(1);
  }, [propSeries, genreFilter, searchTerm]);

  useEffect(() => {
    // Si recibimos series como prop, paginación client-side
    if (propSeries) {
      setLoading(false);

      // Aplico filtro de género sobre propSeries
      const filtered = (() => {
        if (!genreFilter?.trim()) return propSeries;
        const needle = genreFilter.toLowerCase();
        return propSeries.filter((item) => {
          const raw = item.genres ?? item.genre;
          if (!raw) return false;
          if (typeof raw === "string")
            return raw.toLowerCase().includes(needle);
          if (Array.isArray(raw))
            return raw.some((g) => String(g).toLowerCase().includes(needle));
          return false;
        });
      })();

      setSeries(filtered);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE)));
      return;
    }

    // Si no hay propSeries, fetch al backend (server-side)
    const fetchSeries = async () => {
      try {
        setLoading(true);

        let response;
        if (searchTerm?.trim()) {
          response = await searchSeries(
            searchTerm,
            currentPage,
            ITEMS_PER_PAGE
          );
        } else {
          response = await getAllSeries(currentPage, ITEMS_PER_PAGE);
        }

        if (response && response.data) {
          let fetched = response.data;

          // Filtro de género client-side sobre la página actual
          if (genreFilter?.trim()) {
            const needle = genreFilter.toLowerCase();
            fetched = fetched.filter((item) => {
              const raw = item.genres ?? item.genre;
              if (!raw) return false;
              if (typeof raw === "string")
                return raw.toLowerCase().includes(needle);
              if (Array.isArray(raw))
                return raw.some((g) =>
                  String(g).toLowerCase().includes(needle)
                );
              return false;
            });
          }

          setSeries(fetched);

          // Meta desde API (preferible totalPages; si no, totalItems)
          const meta = response.meta || response.pagination || {};
          const apiTotalPages =
            meta.totalPages ??
            (meta.totalItems
              ? Math.ceil(meta.totalItems / ITEMS_PER_PAGE)
              : undefined);

          // Si no viene nada, al menos 1
          setTotalPages(Math.max(1, apiTotalPages || 1));
        } else {
          setError("Error al cargar series");
          setSeries([]);
          setTotalPages(1);
        }
      } catch (err) {
        console.error("Error al obtener series:", err);
        setError("Error al cargar series. Intente nuevamente más tarde.");
        setSeries([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchSeries();
  }, [currentPage, searchTerm, genreFilter, propSeries]);

  const currentSeries = useMemo(() => {
    // Si usamos propSeries (paginación client-side), cortamos acá
    if (propSeries) {
      return series.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      );
    }
    // Si es server-side, la página ya viene cortada por el backend
    return series;
  }, [series, currentPage, propSeries]);

  const handlePageChange = (pageNumber) => {
    // 👈 SIEMPRE permitimos cambiar de página (antes estaba bloqueado con !propSeries)
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <Loader />;

  if (error) return <div className="text-center text-white my-8">{error}</div>;

  if (currentSeries.length === 0)
    return (
      <div className="text-center text-white my-8">
        No hay series disponibles
      </div>
    );

  return (
    <div className="series-list-container bg-gray-900 min-h-screen p-6">
      <div className="container mx-auto">
        <h1 className="text-white text-3xl font-bold mb-8">Series</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {currentSeries.map((item) => (
            <SeriesCard key={item.id} series={item} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination-container mt-8 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesList;

// import React, { useState, useEffect } from "react";
// import { getAllSeries, searchSeries } from "../services/seriesService";
// import SeriesCard from "./SeriesCard";
// import Loader from "./Loader/Loader";
// import Pagination from "./Pagination";

// const SeriesList = ({
//   searchTerm = "",
//   genreFilter = "",
//   series: propSeries = null,
// }) => {
//   const [series, setSeries] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const [itemsPerPage] = useState(12); // Cantidad de series por página

//   useEffect(() => {
//     // Si recibimos series como prop, las usamos directamente
//     if (propSeries) {
//       setSeries(propSeries);
//       setLoading(false);
//       // Ajustamos la paginación (simulada en este caso)
//       setTotalPages(Math.ceil(propSeries.length / itemsPerPage));
//       return;
//     }

//     // Si no hay props, hacemos el fetch normal
//     const fetchSeries = async () => {
//       try {
//         setLoading(true);
//         console.log("SeriesList: Iniciando petición a getAllSeries", {
//           currentPage,
//           itemsPerPage,
//           searchTerm,
//         });

//         // Log para ver la URL y las cabeceras
//         console.log(
//           "API_URL:",
//           process.env.REACT_APP_API_URL || "http://localhost:4000"
//         );

//         let response;
//         if (searchTerm) {
//           response = await searchSeries(searchTerm, currentPage, itemsPerPage);
//         } else {
//           response = await getAllSeries(currentPage, itemsPerPage);
//         }

//         console.log("SeriesList: Respuesta completa de API:", response);

//         if (response && response.data) {
//           let filteredSeries = response.data;

//           // Aplicar filtro de género si se proporciona
//           // Aplicar filtro de género si se proporciona
//           if (genreFilter && genreFilter.trim() !== "") {
//             const needle = genreFilter.toLowerCase();
//             filteredSeries = filteredSeries.filter((item) => {
//               const raw = item.genres ?? item.genre; // 👈 acepta ambos
//               if (typeof raw === "string") {
//                 return raw.toLowerCase().includes(needle);
//               }
//               if (Array.isArray(raw)) {
//                 return raw.some((g) =>
//                   String(g).toLowerCase().includes(needle)
//                 );
//               }
//               return false;
//             });
//           }

//           // if (genreFilter && genreFilter.trim() !== '') {
//           //   const genreLower = genreFilter.toLowerCase();
//           //   filteredSeries = filteredSeries.filter(item => {
//           //     if (typeof item.genres === 'string') {
//           //       return item.genres.toLowerCase().includes(genreLower);
//           //     }
//           //     if (Array.isArray(item.genres)) {
//           //       return item.genres.some(g => g.toLowerCase().includes(genreLower));
//           //     }
//           //     return false;
//           //   });
//           // }

//           setSeries(filteredSeries);

//           // Calcular el número total de páginas
//           const totalItems =
//             response.pagination?.totalItems || filteredSeries.length;
//           setTotalPages(Math.ceil(totalItems / itemsPerPage));
//         } else {
//           setError("Error al cargar series");
//           setSeries([]);
//         }
//       } catch (err) {
//         console.error("Error al obtener series:", err);
//         console.error("Error detallado:", {
//           mensaje: err.message,
//           status: err.response?.status,
//           statusText: err.response?.statusText,
//           data: err.response?.data,
//         });
//         setError("Error al cargar series. Intente nuevamente más tarde.");
//         setSeries([]);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSeries();
//   }, [currentPage, itemsPerPage, searchTerm, genreFilter, propSeries]);

//   const handlePageChange = (pageNumber) => {
//     // Solo cambiar página si no estamos usando series propias
//     if (!propSeries) {
//       setCurrentPage(pageNumber);
//       // Scroll hacia arriba cuando se cambia de página
//       window.scrollTo(0, 0);
//     }
//   };

//   if (loading) {
//     return <Loader />;
//   }

//   if (error) {
//     return <div className="text-center text-white my-8">{error}</div>;
//   }

//   if (series.length === 0) {
//     return (
//       <div className="text-center text-white my-8">
//         No hay series disponibles
//       </div>
//     );
//   }

//   // Obtener solo las series para la página actual cuando usamos propSeries
//   const currentSeries = propSeries
//     ? propSeries.slice(
//         (currentPage - 1) * itemsPerPage,
//         currentPage * itemsPerPage
//       )
//     : series;

//   return (
//     <div className="series-list-container bg-gray-900 min-h-screen p-6">
//       <div className="container mx-auto">
//         <h1 className="text-white text-3xl font-bold mb-8">Series</h1>

//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
//           {currentSeries.map((item) => (
//             <SeriesCard key={item.id} series={item} />
//           ))}
//         </div>

//         {totalPages > 1 && (
//           <div className="pagination-container mt-8 flex justify-center">
//             <Pagination
//               currentPage={currentPage}
//               totalPages={totalPages}
//               onPageChange={handlePageChange}
//             />
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default SeriesList;
